import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@shared";
import { AccountForm } from "@accounts/forms/AccountForm";
import { ProfilePicture } from "@profile";
import { AccountLinkingForm } from "@accounts/forms/GoogleLinkingForm";
import { AccountPasswordForm } from "@accounts/forms/AccountPasswordForm";
import { RadixButton as Button, Badge, Card } from "@printtat/ui";

export const AccountPage = () => {
  const navigate = useNavigate();
  const { user, loading, error, checkAuth } = useAuth();
  const [isEditing, setIsEditing] = useState(false);

  // Automatically redirect to sign-in if no user is found
  if (!loading && !user) {
    navigate("/sign-in", { replace: true });
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button
            onClick={() => navigate("/sign-in")}
            radius="full"
            color="indigo"
            variant="solid"
          >
            Go to Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }



  const getAccountType = () => {
    if (user.googleId) {
      return user.password ? "Google + Password" : "Google Only";
    }
    return "Email/Password";
  };

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-600">Manage your account settings</p>
      </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-6">
          {/* Authentication Methods Card */}
          <Card size="4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Authentication Methods
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email/Password
                </label>
                <div className="mt-1 flex items-center space-x-2">
                  <Badge 
                    color={user.password ? "green" : "gray"}
                    variant="soft"
                  >
                    {user.password ? "Enabled" : "Not Set"}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Google OAuth
                </label>
                <div className="mt-1 flex items-center space-x-2">
                  <Badge 
                    color={user.googleId ? "green" : "gray"}
                    variant="soft"
                  >
                    {user.googleId ? "Linked" : "Not Linked"}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Profile Picture Section */}
          <Card size="4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Profile Picture
            </h2>
            <ProfilePicture user={user} />
          </Card>

          {/* Profile Information Section */}
          <Card size="4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Profile Information
              </h2>
              <Button
                onClick={() => setIsEditing(!isEditing)}
                radius="full"
                color="indigo"
                variant="solid"
              >
                {isEditing ? "Cancel" : "Edit Profile"}
              </Button>
            </div>
            <AccountForm
              user={user}
              isEditing={isEditing}
              onEditComplete={() => setIsEditing(false)}
            />
          </Card>

          {/* Account Security Section */}
          <Card size="4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Account Security
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Account Type
                </label>
                <p className="mt-1 text-sm text-gray-900">{getAccountType()}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email Verification
                </label>
                <div className="mt-1 flex items-center space-x-2">
                  <Badge 
                    color={user.isVerified ? "green" : "yellow"}
                    variant="soft"
                  >
                    {user.isVerified ? "Verified" : "Pending Verification"}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Account Linking Section */}
          <Card size="4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Account Linking
            </h2>
            <AccountLinkingForm user={user} checkAuth={checkAuth} />
          </Card>

          {/* Password Management Section */}
          <Card size="4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Password Management
            </h2>
            <AccountPasswordForm user={user} />
          </Card>
        </div>
    </>
  );
};
